/**
 * Chart colours — Ubiquiti UniFi palette.
 *
 * Values are Ubiquiti's own hue ramps (`--desktop-color-<hue>-06` for the base
 * step, `-08`/`-09` for the accessible text step) as published on ui.com.
 *
 * Accessibility note: the `06` steps are FILL colours. Several of them —
 * `green-06` at 2.10:1 and `orange-06` at 2.04:1 on white — do not meet even
 * the 3:1 non-text rule, so a bare coloured shape is never a status indicator
 * on its own. Charts always pair colour with a label or legend, and the
 * `*_TEXT` values below are what any on-chart or in-legend text must use.
 */

/**
 * Fixed colours for series that mean the same thing wherever they appear.
 *
 * Categorical series draw from `CHART_COLORS` by index, but these five carry
 * semantics — income is always the green one — so they must not shift when a
 * chart's series order changes.
 */
export const CHART_SERIES_COLORS = {
  income: '#38cc65', // unifi green-06
  expenses: '#f0383b', // unifi red-06
  net: '#006fff', // unifi ublue-06
  savingsRate: '#a354e3', // unifi purple-06
  /** Spend that could not be classified (no budget allocation bucket). */
  unassigned: '#808893', // unifi neutral-08
} as const;

/**
 * Text-safe counterparts (>= 4.5:1 on white). Use these for any amount, label
 * or legend text that is tinted by series — never the `CHART_SERIES_COLORS`
 * values, which are fills.
 */
export const CHART_SERIES_TEXT_COLORS = {
  income: '#258341', // green-08  — 4.77:1
  expenses: '#9c2628', // red-08    — 7.72:1
  net: '#0058cc', // ublue-07  — 6.43:1
  savingsRate: '#683592', // purple-08 — 8.36:1
  unassigned: '#50565e', // neutral-10 — 7.41:1
} as const;

/**
 * Categorical palette, in Ubiquiti's own ordering: blue leads, then green,
 * orange, purple, aqua, lime, red, navy.
 */
export const CHART_COLORS = [
  '#006fff', // ublue-06
  '#38cc65', // green-06
  '#f5a524', // orange-06
  '#a354e3', // purple-06
  '#44c6fd', // aqua-06
  '#afd322', // lime-06
  '#f0383b', // red-06
  '#003d9e', // navy-06
  '#ffdd1a', // yellow-06
  '#0058cc', // ublue-07
  '#258341', // green-08
  '#808893', // neutral-08
] as const;

/** Chart chrome — split lines, axis labels, area fills. */
export const CHART_CHROME = {
  /** Ubiquiti `divider` — the hairline split line. */
  splitLine: '#21232712',
  /** Ubiquiti `dataviz-chart-areas-resting` — area fills under lines. */
  areaFill: '#21232714',
  /** Axis and legend label colour. */
  label: '#50565e',
  /** Panel surface, for tooltip backgrounds. */
  surface: '#ffffff',
  /** Ubiquiti tooltips are dark, even in the light theme. */
  tooltipBg: '#383838',
  tooltipText: '#fafafa',
} as const;

/** Sequential ramp for heatmaps — Ubiquiti `ublue-01` → `ublue-06`. */
export const CHART_HEATMAP_RAMP = ['#e6f1ff', '#006fff'] as const;

/** Default series colour for standalone widgets (ring gauge, sparkline). */
export const CHART_PRIMARY = '#006fff';

/** Marker/annotation colour on charts — text-safe at 7.41:1. */
export const CHART_MARKER = '#50565e';
